/**
 * Core API - User Roles & Permissions
 *
 * Database operations for tenant memberships and factory role assignments.
 */

import { supabase } from '@/integrations/supabase/client';


// =============================================================================
// Types
// =============================================================================

export type FactoryRole = 'supervisor' | 'worker';

export interface UserFactoryRole {
  id: string;
  user_id: string;
  tenant_id: string;
  factory_id: string;
  role: FactoryRole;
  status: 'pending' | 'approved' | 'rejected';
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserFactoryRoleWithDetails {
  id: string;
  factory_id: string;
  tenant_id: string;
  role: FactoryRole;
  status: 'pending' | 'approved' | 'rejected';
  factory: {
    id: string;
    name: string;
    code: string;
    tenant_id: string;
  };
}

export interface LocationAssignment {
  id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  area?: { id: string; name: string } | null;
  line?: { id: string; name: string } | null;
  station?: { id: string; name: string } | null;
}

// =============================================================================
// Queries - User Factory Roles
// =============================================================================

/**
 * Fetches all factory roles for a user (with factory details).
 */
export async function fetchUserFactoryRoles(userId: string): Promise<UserFactoryRoleWithDetails[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('user_factory_roles')
    .select(`
      id,
      factory_id,
      tenant_id,
      role,
      status,
      factory:factories(id, name, code, tenant_id)
    `)
    .eq('user_id', userId);

  if (error) throw error;
  return data as unknown as UserFactoryRoleWithDetails[];
}

/**
 * Fetches a user's permissions for a specific factory based on role.
 */
export async function fetchUserPermissions(userId: string, factoryId: string): Promise<string[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('user_factory_roles')
    .select('role')
    .eq('user_id', userId)
    .eq('factory_id', factoryId)
    .eq('status', 'approved')
    .maybeSingle();

  if (error) throw error;
  if (!data?.role) return [];

  return data.role === 'supervisor' ? ['*'] : [];
}

/**
 * Fetches the user's factory role ID for location assignments.
 */
export async function fetchUserFactoryRoleId(
  userId: string,
  factoryId: string
): Promise<string | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('user_factory_roles')
    .select('id')
    .eq('user_id', userId)
    .eq('factory_id', factoryId)
    .eq('status', 'approved')
    .maybeSingle();

  if (error) throw error;
  return data?.id ?? null;
}

// =============================================================================
// Queries - Location Assignments
// =============================================================================

/**
 * Fetches location assignments for a user factory role.
 */
export async function fetchLocationAssignments(userFactoryRoleId: string): Promise<LocationAssignment[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('user_location_assignments')
    .select(`
      id,
      area_id,
      line_id,
      station_id,
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name)
    `)
    .eq('user_factory_role_id', userFactoryRoleId);

  if (error) throw error;
  return data as unknown as LocationAssignment[];
}

// =============================================================================
// Queries - Factory Users (Admin)
// =============================================================================

/**
 * Fetches all users with their roles for a specific factory (admin view).
 */
export async function fetchFactoryUsers(factoryId: string) {
  const { data, error } = await supabase
    .schema('core')
    .from('user_factory_roles')
    .select(`
      id,
      user_id,
      tenant_id,
      factory_id,
      role,
      status,
      created_at,
      profile:profiles!user_factory_roles_user_id_fkey(id, email, full_name, department, job_title)
    `)
    .eq('factory_id', factoryId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Fetches location assignments for multiple user factory roles.
 */
export async function fetchLocationAssignmentsForUsers(userFactoryRoleIds: string[]) {
  if (userFactoryRoleIds.length === 0) return [];

  const { data, error } = await supabase
    .schema('core')
    .from('user_location_assignments')
    .select('id, user_factory_role_id, area_id, line_id, station_id')
    .in('user_factory_role_id', userFactoryRoleIds);

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Updates a user's factory role (status, role assignment).
 */
export async function updateUserFactoryRole(
  id: string,
  updates: { status?: 'approved' | 'rejected'; role?: FactoryRole | null }
): Promise<void> {
  const payload: Record<string, unknown> = { ...updates };

  if (payload.role === null) {
    delete payload.role;
  }

  const { error } = await supabase
    .schema('core')
    .from('user_factory_roles')
    .update(payload)
    .eq('id', id);

  if (error) throw error;
}

/**
 * Replaces location assignments for a user factory role.
 */
export async function replaceLocationAssignments(
  userFactoryRoleId: string,
  tenantId: string,
  assignments: { area_id?: string; line_id?: string; station_id?: string }[]
): Promise<void> {
  const { error: deleteError } = await supabase
    .schema('core')
    .from('user_location_assignments')
    .delete()
    .eq('user_factory_role_id', userFactoryRoleId);

  if (deleteError) throw deleteError;

  if (assignments.length > 0) {
    const records = assignments.map((assignment) => ({
      user_factory_role_id: userFactoryRoleId,
      tenant_id: tenantId,
      ...assignment,
    }));

    const { error: insertError } = await supabase
      .schema('core')
      .from('user_location_assignments')
      .insert(records);

    if (insertError) throw insertError;
  }
}
